// Background alpha (§11.12, §6.2 pass 16).
//  · BG_REFINE: base matte (model or key) with the refine strokes (keep = max, remove = min).
//  · BG_MINMAX: separable dilate / erode (edge shift).
//  · PUSH / PULL: push-pull fill of the solid-subject colour (alpha ≥ 0.95) into the edge band — the
//    "estimated foreground" that decontaminate blends in, so no backdrop fringe shows.
//  · BG_APPLY: alpha × matte, decontaminate, optional soft contact shadow under the subject.
import { HEADER } from './common';

export const BG_REFINE = `${HEADER}
uniform sampler2D uMatte;
uniform sampler2D uRefine;   // r = keep strokes, g = remove strokes (whole source)
uniform vec2 uSize;
out vec4 o;
void main() {
  vec2 uv = gl_FragCoord.xy / uSize;
  float m = texture(uMatte, uv).r;
  vec4 r = texture(uRefine, uv);
  m = min(max(m, r.r), 1.0 - r.g);
  o = vec4(m, 0.0, 0.0, 1.0);
}`;

export const BG_MINMAX = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uDir;
uniform int uR;
uniform int uDilate;
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  ivec2 sz = textureSize(uSrc, 0);
  float v = texelFetch(uSrc, p, 0).r;
  for (int i = -64; i <= 64; i++) {
    if (i < -uR || i > uR) continue;
    float s = texelFetch(uSrc, clamp(p + ivec2(uDir) * i, ivec2(0), sz - 1), 0).r;
    v = uDilate == 1 ? max(v, s) : min(v, s);
  }
  o = vec4(v, 0.0, 0.0, 1.0);
}`;

/** Level 0 of the push-pull: (straight colour × w, w), w = solid subject (matte ≥ 0.95) × alpha. */
export const PUSH0 = `${HEADER}
uniform sampler2D uImg;
uniform sampler2D uMatte;
uniform mat3 uToSrc;
uniform int uPremul;
uniform int uMatteTile;      // 1: uMatte is tile-sized (full resolution), read per texel
out vec4 o;
void main() {
  vec4 c = texelFetch(uImg, ivec2(gl_FragCoord.xy), 0);
  vec3 rgb = uPremul == 1 ? (c.a > 1e-6 ? c.rgb / c.a : vec3(0.0)) : c.rgb;
  vec3 h = uToSrc * vec3(gl_FragCoord.xy, 1.0);
  float mt = uMatteTile == 1 ? texelFetch(uMatte, ivec2(gl_FragCoord.xy), 0).r : texture(uMatte, h.xy / h.z).r;
  float w = (mt >= 0.95 ? 1.0 : 0.0) * clamp(c.a, 0.0, 1.0);
  o = vec4(rgb * w, w);
}`;

/** Pull: fine (colour × w, w) filled from the coarser level's colour where w < 1. Output straight, w = 1. */
export const PULL = `${HEADER}
uniform sampler2D uFine;
uniform sampler2D uCoarse;
uniform int uCoarsest;
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec4 f = texelFetch(uFine, p, 0);
  if (uCoarsest == 1) { o = vec4(f.a > 1e-6 ? f.rgb / f.a : vec3(0.0), 1.0); return; }
  vec2 uv = (vec2(p) + 0.5) / vec2(textureSize(uFine, 0));
  vec3 c = texture(uCoarse, uv).rgb;
  float w = clamp(f.a, 0.0, 1.0);
  vec3 own = f.a > 1e-6 ? f.rgb / f.a : vec3(0.0);
  o = vec4(mix(c, own, w), 1.0);
}`;

export const BG_APPLY = `${HEADER}
uniform sampler2D uImg;
uniform sampler2D uMatte;
uniform sampler2D uFill;     // push-pull foreground estimate (straight), same size as uImg
uniform sampler2D uShadow;   // blurred matte (whole source)
uniform mat3 uToSrc;
uniform int uPremul;
uniform float uDecon;        // 0..100
uniform int uShadowOn;
uniform float uShadowOpacity; // 0..1
uniform float uShadowOffset;  // source-normalized y offset
uniform int uMatteTile;       // 1: uMatte is tile-sized (full resolution), read per texel
out vec4 o;
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  vec4 c = texelFetch(uImg, p, 0);
  float a = clamp(c.a, 0.0, 1.0);
  vec3 rgb = uPremul == 1 ? (a > 1e-6 ? c.rgb / a : vec3(0.0)) : c.rgb;
  vec3 h = uToSrc * vec3(gl_FragCoord.xy, 1.0);
  vec2 uv = h.xy / h.z;
  float m = uMatteTile == 1 ? texelFetch(uMatte, p, 0).r : texture(uMatte, uv).r;
  if (uDecon > 0.0 && m > 0.02 && m < 0.98) {
    float t = clamp((m - 0.6) / 0.38, 0.0, 1.0);
    float w = uDecon / 100.0 * (1.0 - t * t * (3.0 - 2.0 * t));
    rgb = mix(rgb, texelFetch(uFill, p, 0).rgb, w);
  }
  float a2 = a * m;
  vec3 col = rgb * a2;            // premultiplied subject
  float A = a2;
  if (uShadowOn == 1) {
    float s = texture(uShadow, uv - vec2(0.0, uShadowOffset)).r * uShadowOpacity * a;
    A = a2 + s * (1.0 - a2);       // subject over a black shadow
  }
  o = uPremul == 1 ? vec4(col, A) : vec4(A > 1e-6 ? col / A : rgb, A);
}`;

/** 4×4-sample box downscale of the R channel (the shadow is computed at ≤ 512 px). */
export const BOX_DOWN = `${HEADER}
uniform sampler2D uSrc;
uniform vec2 uOutSize;
out vec4 o;
void main() {
  vec2 sz = vec2(textureSize(uSrc, 0));
  vec2 f = sz / uOutSize;
  float acc = 0.0;
  for (int j = 0; j < 4; j++)
    for (int i = 0; i < 4; i++)
      acc += texture(uSrc, (floor(gl_FragCoord.xy) * f + (vec2(float(i), float(j)) + 0.5) * f / 4.0) / sz).r;
  o = vec4(acc / 16.0, 0.0, 0.0, 1.0);
}`;

// ── Full-resolution matte per tile (CODE_REVIEW D18/D15) ─────────────────────────────────────────
// The cached matte is ≤ 2048 px. For export tiles and the 100% view it is re-estimated at full resolution
// inside its soft band: each band pixel's alpha is the projection of its own colour onto the line between
// the mean solid-subject colour (matte ≥ 0.95) and the mean solid-backdrop colour (matte ≤ 0.05) found in
// a window around it (a local two-colour model, in encoded RGB, so it also separates edges of equal
// brightness). Where the two colours are too close to tell apart the enlarged matte is kept. The refine
// strokes are then applied at full resolution (keep = max, remove = min).
export const BG_SHARP = `${HEADER}
uniform sampler2D uImg;
uniform sampler2D uMatte;     // refined matte, ≤ 2048 px, whole source
uniform sampler2D uRefine;    // r = keep, g = remove, tile-sized
uniform mat3 uToSrc;
uniform int uPremul;
uniform int uR;               // window radius in tile pixels (≤ 48)
uniform int uStep;            // tap spacing
uniform int uHasRefine;
uniform float uSoft;          // 0..1: an edge feather asks for the soft enlarged matte back
out vec4 o;
vec3 enc(vec4 c) {
  float a = clamp(c.a, 0.0, 1.0);
  vec3 rgb = uPremul == 1 ? (a > 1e-6 ? c.rgb / a : vec3(0.0)) : c.rgb;
  return pow(clamp(rgb, 0.0, 1.0), vec3(1.0 / 2.2));
}
float matteAt(vec2 frag) {
  vec3 h = uToSrc * vec3(frag, 1.0);
  return texture(uMatte, h.xy / h.z).r;
}
void main() {
  ivec2 p = ivec2(gl_FragCoord.xy);
  ivec2 sz = textureSize(uImg, 0);
  float m = matteAt(gl_FragCoord.xy);
  float q = m;
  if (m > 0.02 && m < 0.98) {
    vec3 F = vec3(0.0), B = vec3(0.0);
    float nf = 0.0, nb = 0.0;
    for (int j = -uR; j <= uR; j += uStep) {
      for (int i = -uR; i <= uR; i += uStep) {
        ivec2 t = clamp(p + ivec2(i, j), ivec2(0), sz - 1);
        float mt = matteAt(vec2(t) + 0.5);
        if (mt >= 0.95) { F += enc(texelFetch(uImg, t, 0)); nf += 1.0; }
        else if (mt <= 0.05) { B += enc(texelFetch(uImg, t, 0)); nb += 1.0; }
      }
    }
    if (nf > 0.0 && nb > 0.0) {
      F /= nf;
      B /= nb;
      vec3 d = F - B;
      float dd = dot(d, d);
      float est = clamp(dot(enc(texelFetch(uImg, p, 0)) - B, d) / max(dd, 1e-6), 0.0, 1.0);
      q = mix(m, est, smoothstep(0.03, 0.1, sqrt(dd)));
    }
  }
  q = mix(q, m, uSoft);
  if (uHasRefine == 1) {
    vec4 r = texelFetch(uRefine, p, 0);
    q = min(max(q, r.r), 1.0 - r.g);
  }
  o = vec4(q, 0.0, 0.0, 1.0);
}`;
